/**
 * スライドショーを生成し、Lightbox連携を行うヘルパー関数。
 * Lightboxのオプションはここで個別に設定可能です。
 *
 * @param {string} containerId スライドショーを挿入するHTML要素のID
 * @param {Array<Array<string>>} images 画像情報の配列。各要素は [画像のURL, タイトル文字列（ブランク可）]
 * @param {number} intervalSeconds スライドの自動切り替え秒数
 * @param {object} [lightboxOptions={}] Lightboxのオプションを個別に指定するオブジェクト（任意）
 */
export function generateSlideshow(containerId, images, intervalSeconds, lightboxOptions = {}) {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container with ID "${containerId}" not found.`);
    return;
  }

  // 表示する画像がない場合はメッセージを表示して終了
  if (images.length === 0) {
    container.innerHTML = '<p>表示する画像がありません。</p>';
    return;
  }

  let currentIndex = 0;
  let slideshowInterval;

  // 既存のスライドショーコンテンツをクリア
  container.innerHTML = '';

  // 画像要素の生成と追加
  images.forEach((imageData, index) => {
    const imageUrl = imageData[0];
    const title = imageData[1] || ''; // タイトルがブランクの場合は空文字列

    // ⭐ Lightbox 2 が期待する <a> タグを生成 ⭐
    const anchor = document.createElement('a');
    anchor.href = imageUrl; // 拡大表示したい画像のURLを href に設定
    anchor.setAttribute('data-lightbox', 'shop-slideshow-gallery'); // Lightbox ギャラリー名を指定

    if (title) {
      anchor.setAttribute('data-title', title); // Lightbox のキャプションとしてタイトルを設定
    }

    // 個別の Lightbox オプションが指定されていれば <a> タグに設定
    if (Object.keys(lightboxOptions).length > 0) {
      anchor.setAttribute('data-lightbox-options', JSON.stringify(lightboxOptions));
    }

    // <img> タグを生成
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = title; // alt属性も設定
    img.classList.add('slideshow-image'); // スライドショー用のCSSクラスを付与

    // 最初の画像をアクティブにする（スライドショーの表示制御用）
    if (index === 0) {
      img.classList.add('active');
    }

    // <a> タグの中に <img> タグを挿入
    anchor.appendChild(img);
    // コンテナに <a> タグを追加
    container.appendChild(anchor);
  });

  const slideshowImages = container.getElementsByClassName('slideshow-image');

  // スライド切り替えロジック
  function showNextSlide() {
    if (slideshowImages.length === 0) return; // 画像がない場合は何もしない

    // 現在のアクティブな画像から 'active' クラスを削除
    slideshowImages[currentIndex].classList.remove('active');

    // 次のインデックスを計算（ループ）
    currentIndex = (currentIndex + 1) % slideshowImages.length;

    // 次の画像に 'active' クラスを追加
    slideshowImages[currentIndex].classList.add('active');
  }

  // スライドショーの開始
  function startSlideshow() {
    if (slideshowImages.length > 1) { // 画像が1枚だけなら自動切り替えは不要
      stopSlideshow(); // 既にタイマーが動いている可能性があるので一度停止
      slideshowInterval = setInterval(showNextSlide, intervalSeconds * 1000);
    }
  }

  // スライドショーの停止
  function stopSlideshow() {
    clearInterval(slideshowInterval);
  }

  // マウスオーバーで一時停止、マウスアウトで再開
  container.addEventListener('mouseenter', stopSlideshow);
  container.addEventListener('mouseleave', startSlideshow);

  // 初期スライドショー開始
  startSlideshow();

  // ⭐⭐ jQuery が存在する場合、Lightbox 2 の再スキャンを促すためのワークアラウンド ⭐⭐
  // (これが最も安全で簡単な解決策です。jQuery がない場合は、Lightbox 2 は動的コンテンツに対応しません。)
  if (typeof jQuery !== 'undefined') {
    // Lightbox 2 は $(document).ready() で初期化されるため、
    // ready イベントを強制的にトリガーして再スキャンを促す。
    // これは公式の Lightbox API ではなく、内部的な動作を利用するハックです。
    // Lightbox 2 の問題点として、DOM追加後の再スキャンに弱いことが挙げられます。
    jQuery(document).trigger('ready');
    console.log('Lightbox 2: jQuery ready event manually triggered for dynamic content.');
  } else {
    console.warn("Lightbox 2: jQuery is not loaded. Cannot manually trigger 'ready' event for Lightbox.");
    console.warn("Please note that Lightbox 2 has limited support for dynamically added content without jQuery or a manual re-initialization hack.");
  }
}